<template>
</template>

<script setup lang="ts">
import GlobalBizServer from '~/services/GlobalBizServer'
import useUserStore from '~/store/modules/user'
import { getEnterpriseFuelMode, getGlobalFuelAutomaticAllocation } from '~/utils/enterpriseTrade'

const userStore = useUserStore()
const { token, isUserInfoReady, bizTeamInfo } = storeToRefs(userStore)
const checking = ref(false)
const initialCheckedToken = ref('')
const PAYMENT_RETRY_LIMIT = 5
const PAYMENT_RETRY_DELAY = 2000
let paymentRetryTimer: ReturnType<typeof setTimeout> | null = null
let paymentRetryRun = 0

async function checkPendingFuel(): Promise<boolean> {
  if (checking.value || !token.value || !isUserInfoReady.value
    || getEnterpriseFuelMode(bizTeamInfo.value) !== 'global') return false
  checking.value = true
  try {
    const response = await GlobalBizServer.pendingFuel()
    if (response.code !== '1' || !response.data) return false

    const fuelPackage = response.data
    if (!['ALLOCATION_PENDING', 'PROVISION_FAILED'].includes(fuelPackage.status)) return true

    const allocation = getGlobalFuelAutomaticAllocation(
      fuelPackage.totalQuota,
      fuelPackage.aiQuota,
      fuelPackage.vipQuota,
    )
    const allocationResponse = await GlobalBizServer.allocateFuel({
      packageNo: fuelPackage.packageNo,
      ...allocation,
    })
    if (allocationResponse.code === '1' && allocationResponse.data?.status === 'ACTIVE') {
      try {
        await userStore.getBizTeamInfo()
      } catch {
        // 加油包已完成双区入账，企业信息仍会在后续全局刷新中同步。
      }
    }
    return true
  } catch {
    // Java 会后台重试自动分账，前端补偿失败时不打断用户操作。
    return false
  } finally {
    checking.value = false
  }
}

function clearPaymentRetry() {
  paymentRetryRun += 1
  if (!paymentRetryTimer) return
  clearTimeout(paymentRetryTimer)
  paymentRetryTimer = null
}

async function checkPendingFuelAfterPayment(attempt = 1, runId = paymentRetryRun) {
  if (runId !== paymentRetryRun) return
  const foundPendingFuel = await checkPendingFuel()
  if (runId !== paymentRetryRun) return
  if (foundPendingFuel || attempt >= PAYMENT_RETRY_LIMIT || !token.value) {
    clearPaymentRetry()
    return
  }
  paymentRetryTimer = setTimeout(() => {
    void checkPendingFuelAfterPayment(attempt + 1, runId)
  }, PAYMENT_RETRY_DELAY)
}

function handlePaymentRefresh(event: MessageEvent) {
  if (event.origin !== window.location.origin || event.data?.refresh !== true) return
  clearPaymentRetry()
  void checkPendingFuelAfterPayment()
}

watch([token, isUserInfoReady, bizTeamInfo], ([nextToken, ready, enterprise], previousValues = []) => {
  const previousToken = previousValues[0]
  if (nextToken !== previousToken) {
    initialCheckedToken.value = ''
    clearPaymentRetry()
  }
  if (!nextToken || !ready || getEnterpriseFuelMode(enterprise) !== 'global') {
    initialCheckedToken.value = ''
    return
  }
  if (initialCheckedToken.value === nextToken) return
  initialCheckedToken.value = nextToken
  void checkPendingFuel()
}, { immediate: true })

onMounted(() => window.addEventListener('message', handlePaymentRefresh))

onBeforeUnmount(() => {
  clearPaymentRetry()
  window.removeEventListener('message', handlePaymentRefresh)
})
</script>
